import {
  BookingLocationType,
  UserGroupDataQualityPersonalSubject,
  type UserGroupDataQualityField,
  type UserGroupDataQualityIssueRecord,
  type UserGroupDataQualityRecord
} from '@navio/nsdk';
import { getUserGroupDataQualityIssueLabel, useAuthContext, useIsGroupAdmin, useUserGroupDataQuality } from 'noddi-web';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { useIsDataQualityPromptDisabled } from '../../stores/dataQualityPromptStore';
import { getBlockingCustomerDataQualityIssues } from './customerDataQualityFixes';
import { DataQualityFixDialog } from './DataQualityFixDialog';
import { getUserGroupDataQualityTranslations } from './translations';

export type CustomerDataQualityCheckoutProps = {
  userGroupId?: number | null;
  /** The booking this gate is for. A mobile booking is not evaluated at all. */
  bookingLocationType?: BookingLocationType;
  /** Booking brand when the page brand has not caught up yet (localhost). */
  brandDomain?: string;
  countryCode?: string;
  /** Asks for the per-member rows. Ignored unless the session administers the group — the backend only serves them to admins. */
  includeMembers?: boolean;
  /** Opens the sheet as soon as the report has a fixable issue (booking page, personal home). */
  openWhenBlocked?: boolean;
};

/** Confirm and pay pass this. A banner open leaves it unset, so the last step only saves. */
type DataQualityResume = {
  willPay: boolean;
  /** Runs after the last step is saved. Closing the sheet does not call this. */
  onSaved?: () => void;
};

/**
 * What a confirm/pay click should do next. `wait` only happens if a click lands
 * while the report is still loading — every CTA disables on `isChecking`, so it
 * is a guard rather than a path.
 */
type DataQualityHold = 'opened' | 'wait' | 'clear';

export type CustomerDataQualityGate = {
  /** The one report every surface under the provider renders and gates on. */
  record: UserGroupDataQualityRecord | undefined;
  hasError: boolean;
  /** Whether the optional banner and auto-open prompt are visible. */
  showNotices: boolean;
  showMembers: boolean;
  /**
   * Organizations are advised, not stopped: the banner reports, the sheet opens
   * only if they ask for it, and no click is ever held. Their details are
   * maintained by an admin rather than by whoever is checking out, so blocking
   * the person at the till on data they may not own strands the booking.
   */
  isAdvisory: boolean;
  /** Issues the sheet can fix. Empty while loading or when the brand's prompt is off. */
  blockingIssues: UserGroupDataQualityIssueRecord[];
  canFix: boolean;
  isChecking: boolean;
  openFix: () => void;
  holdForDataQuality: (resume: DataQualityResume) => DataQualityHold;
  dialog: ReactNode;
};

/** No provider above: nothing to check, nothing to render, every click proceeds. */
const inertGate: CustomerDataQualityGate = {
  record: undefined,
  hasError: false,
  showNotices: false,
  showMembers: false,
  isAdvisory: false,
  blockingIssues: [],
  canFix: false,
  isChecking: false,
  openFix: () => {},
  holdForDataQuality: () => 'clear',
  dialog: null
};

/**
 * The only customer fix for a data-quality report. One mount owns the query and
 * the sheet: the banner and every confirm, pay, and invoice click read this gate.
 * Steps are the issues `GET /v1/user-groups/data-quality/` still reports. A brand
 * with the prompt off reports every group valid, so nothing opens. A group the
 * session is not a member of is not checked at all: see `canCheck` below, and an
 * organization is reported to its admins alone, and only ever advised: see
 * `isAdvisory`.
 *
 * Phone uses the bottom sheet inside DataQualityFixDialog; md and up use the dialog.
 */
export function useCustomerDataQualityCheckout({
  userGroupId = null,
  bookingLocationType,
  brandDomain,
  countryCode,
  includeMembers = false,
  openWhenBlocked = false
}: CustomerDataQualityCheckoutProps): CustomerDataQualityGate {
  const { userData } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const isPromptDisabled = useIsDataQualityPromptDisabled(userData?.user.id);
  const sessionUser = userData?.user;
  const isSignedIn = sessionUser != null;
  const showNotices = !isPromptDisabled;

  // The backend serves this report on plain membership, so the group has to be
  // one of the session's own. A booking page reached by its public slug is open
  // to anyone holding the link — a forwarded payment SMS lands a signed-in user
  // on someone else's group, and asking anyway is a 403 the user cannot act on.
  const sessionUserGroup = sessionUser?.userGroups.find((userGroup) => userGroup.id === userGroupId);
  const bypassesMembership = sessionUser?.isStaff === true || sessionUser?.isSuperuser === true;
  const isMemberOfGroup = sessionUserGroup != null;

  // An organization's details belong to the people who maintain them. Only the
  // group's admins are reported; a driver booking for the fleet is told nothing
  // about data they cannot own. The lookup runs for the group's own members
  // only — asking it for a group the session is not in is the same 403 the
  // report itself would be.
  const isOrganizationGroup = sessionUserGroup?.isOrganization === true;
  const shouldLookUpAdmin = isOrganizationGroup && isMemberOfGroup;
  const { isGroupAdmin, isLoading: isGroupAdminLoading } = useIsGroupAdmin(
    shouldLookUpAdmin ? (userGroupId ?? undefined) : undefined,
    shouldLookUpAdmin ? sessionUser?.id : undefined
  );
  // The login payload answers at once so nothing flickers; the lookup wins once
  // settled, which catches a role changed since the session began.
  const administersGroup =
    isGroupAdminLoading || isGroupAdmin === undefined ? sessionUserGroup?.isAdmin === true : isGroupAdmin;

  const canCheck = isSignedIn && (isMemberOfGroup || bypassesMembership) && (!isOrganizationGroup || administersGroup);
  // Member rows are people data: the backend serves them to the group's admins only.
  const canIncludeMembers = includeMembers && (administersGroup || bypassesMembership);
  // A mobile booking is driven to an address we already hold and invoiced from
  // it, so it asks for nothing: no report, no banner, no held click. Only a
  // stationary booking, and the surfaces with no booking at all (the home
  // page), evaluate one.
  const isMobileBooking = bookingLocationType === BookingLocationType.MOBILE;
  const [open, setOpen] = useState(false);
  const [didAutoOpen, setDidAutoOpen] = useState(false);
  const [checkoutResume, setCheckoutResume] = useState<DataQualityResume | null>(null);

  const query = useUserGroupDataQuality({
    brandDomain: brandDomain ?? currentBrand,
    bookingLocationType,
    personalSubject: UserGroupDataQualityPersonalSubject.REQUESTING_USER,
    includeMembers: canIncludeMembers,
    userGroupId,
    enabled: canCheck && !isMobileBooking
  });

  // The report answers this once it lands; until then the session's own record
  // of the group does, so the first click is judged by the same rule as the last.
  const isAdvisory = query.record ? !query.record.isPersonal : sessionUserGroup?.isOrganization === true;

  const blockingIssues = getBlockingCustomerDataQualityIssues(query.record?.issues ?? []);
  const canFix = blockingIssues.length > 0;
  const isChecking = query.isLoading;

  // Derived until dismissed: no render/effect state synchronization is needed.
  // An organization is never opened on arrival — the banner is the whole prompt.
  const isAutoOpen = openWhenBlocked && showNotices && canFix && !isAdvisory && !didAutoOpen;

  // A saved value can still be invalid (a placeholder email passes the format
  // check). The report is the source of truth, so the sheet stays until it clears.
  const recheckStep = async (field: UserGroupDataQualityField): Promise<string | null> => {
    const texts = getUserGroupDataQualityTranslations();
    const result = await query.refetch();
    const record = result.data?.[0];
    if (result.error || !record) {
      return texts.errorTitle;
    }
    const issue = getBlockingCustomerDataQualityIssues(record.issues).find((candidate) => candidate.field === field);
    if (!issue) {
      return null;
    }
    return getUserGroupDataQualityIssueLabel(texts, issue);
  };

  const close = () => {
    setOpen(false);
    setDidAutoOpen(true);
    setCheckoutResume(null);
  };

  const holdForDataQuality = (resume: DataQualityResume): DataQualityHold => {
    if (!canCheck || isMobileBooking || userGroupId == null || query.isError || isAdvisory) {
      return 'clear';
    }
    if (canFix) {
      setCheckoutResume(resume);
      setOpen(true);
      return 'opened';
    }
    return isChecking ? 'wait' : 'clear';
  };

  const openFix = () => {
    if (canFix) {
      setCheckoutResume(null);
      setOpen(true);
    }
  };

  const dialog = userGroupId != null && (
    <DataQualityFixDialog
      open={open || isAutoOpen}
      onClose={close}
      userGroupId={userGroupId}
      countryCode={countryCode}
      resumesCheckout={checkoutResume != null}
      willPay={checkoutResume?.willPay ?? false}
      dataQualityIssues={blockingIssues}
      onRecheckStep={recheckStep}
      onSaved={() => {
        const saved = checkoutResume?.onSaved;
        close();
        saved?.();
      }}
    />
  );

  return {
    record: query.record,
    hasError: query.isError,
    showNotices,
    showMembers: canIncludeMembers,
    isAdvisory,
    blockingIssues,
    canFix,
    isChecking,
    holdForDataQuality,
    openFix,
    dialog
  };
}

export const customerDataQualityContext = createContext<CustomerDataQualityGate | null>(null);

/**
 * The gate for the surface this component sits in. Outside a provider it is
 * inert, so a shared CTA can read it unconditionally without a runtime throw.
 */
export function useCustomerDataQualityGate(): CustomerDataQualityGate {
  return useContext(customerDataQualityContext) ?? inertGate;
}
